import React from "react";
import { withRouter } from "react-router-dom";

import Backend from "../../../../utils/Backend";
import Notify from "../../../../utils/Notify";
import General from "../../../../utils/General";
import Currency from "../../../../utils/Currency";

import BaseTable from "../BaseTable";

const BASKET_TYPE_OPTIONS = [
  { label: "All Types", value: "" },
  { label: "Hamper", value: "hamper" },
  { label: "Party Basket", value: "party_basket" },
  { label: "Custom Bundle", value: "custom_bundle" },
];

const STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" },
];

class BasketsTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      searchTerm: "",
      filterType: "",
      filterStatus: "",
    };

    this.table = React.createRef();
  }

  _toggleBasketVisibility(basket) {
    const newStatus = !basket.is_active;
    const actionText = newStatus ? "activate" : "deactivate";

    Backend.toggleBasketVisibility(basket.id, newStatus)
      .then(() => {
        Notify.success(`Basket ${actionText}d successfully`);
        this._refreshTable();
      })
      .catch((error) => {
        Notify.error(error.message || `Failed to ${actionText} basket`);
      });
  }

  _deleteBasket(basket) {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${basket.name}"?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    Backend.deleteBasket(basket.id)
      .then(() => {
        Notify.success("Basket deleted successfully");
        this._refreshTable();
      })
      .catch((error) => {
        Notify.error(error.message || "Failed to delete basket");
      });
  }

  _refreshTable() {
    if (this.table.current) {
      this.table.current.refresh();
    } else {
      window.location.reload();
    }
  }

  _getColumns() {
    return [
      {
        Header: () => <strong className="header__clr">BASKET</strong>,
        id: "name",
        Cell: (rowInfo) => {
          const basket = rowInfo.original;
          const initials = General.getInitials(basket.name);
          const image = basket?.image?.thumbnail || basket?.image?.original;

          return (
            <div className="d-flex align-items-center">
              <div className="symbol symbol-50 symbol-light mr-3">
                {image ? (
                  <img
                    src={image}
                    alt={basket.name}
                    className="symbol-label"
                    style={{ objectFit: "cover" }}
                  />
                ) : (
                  <span className="symbol-label font-size-h5 font-weight-bold">
                    {initials}
                  </span>
                )}
              </div>
              <div className="d-flex flex-column">
                <span className="text-dark-75 font-weight-bold">
                  {basket.name}
                </span>
                <span className="text-muted font-size-sm">
                  {basket.description?.substring(0, 50)}
                  {basket.description?.length > 50 ? "..." : ""}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        Header: () => <strong className="header__clr">TYPE</strong>,
        id: "type",
        width: 120,
        Cell: (rowInfo) => {
          const basket = rowInfo.original;
          const typeLabels = {
            hamper: "Hamper",
            party_basket: "Party Basket",
            custom_bundle: "Custom Bundle",
          };
          const typeColors = {
            hamper: "label-light-primary",
            party_basket: "label-light-success",
            custom_bundle: "label-light-warning",
          };

          return (
            <span
              className={`label label-lg ${
                typeColors[basket.type] || "label-light-info"
              } label-inline`}
            >
              {typeLabels[basket.type] || basket.type}
            </span>
          );
        },
      },
      {
        Header: () => <strong className="header__clr">PRODUCTS</strong>,
        id: "items_count",
        width: 100,
        Cell: (rowInfo) => {
          const basket = rowInfo.original;
          const itemsCount = basket.items?.length || basket.items_count || 0;

          return (
            <span className="font-weight-bold text-dark-75">
              {itemsCount} items
            </span>
          );
        },
      },
      {
        Header: () => <strong className="header__clr">PRICE</strong>,
        id: "price",
        width: 150,
        Cell: (rowInfo) => {
          const basket = rowInfo.original;
          const hasDiscount =
            basket.discounted_price && basket.discounted_price < basket.price;

          return (
            <div className="d-flex flex-column">
              {hasDiscount ? (
                <>
                  <span className="text-dark-75 font-weight-bold">
                    {Currency.format(basket.discounted_price)}
                  </span>
                  <span
                    className="text-muted font-size-sm"
                    style={{ textDecoration: "line-through" }}
                  >
                    {Currency.format(basket.price)}
                  </span>
                </>
              ) : (
                <span className="text-dark-75 font-weight-bold">
                  {Currency.format(basket.price)}
                </span>
              )}
            </div>
          );
        },
      },
      {
        Header: () => <strong className="header__clr">STATUS</strong>,
        id: "is_active",
        width: 100,
        Cell: (rowInfo) => {
          const basket = rowInfo.original;

          return (
            <span
              className={`label label-lg ${
                basket.is_active ? "label-light-success" : "label-light-danger"
              } label-inline`}
            >
              {basket.is_active ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        Header: () => <strong className="header__clr">ACTIONS</strong>,
        id: "actions",
        width: 180,
        sortable: false,
        Cell: (rowInfo) => {
          const basket = rowInfo.original;

          return (
            <div className="d-flex">
              {/* View Details Button */}
              <button
                type="button"
                className="btn btn-icon btn-sm btn-pill btn-outline-primary mr-2"
                title="View Details"
                onClick={() =>
                  this.props.history.push(`/manage-baskets/${basket.id}/details`)
                }
              >
                <span className="svg-icon svg-icon-md">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24px"
                    height="24px"
                    viewBox="0 0 24 24"
                  >
                    <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                      <rect x={0} y={0} width={24} height={24} />
                      <path
                        d="M12,4.5 C7,4.5 2.73,7.61 1,12 C2.73,16.39 7,19.5 12,19.5 C17,19.5 21.27,16.39 23,12 C21.27,7.61 17,4.5 12,4.5 Z M12,17 C9.24,17 7,14.76 7,12 C7,9.24 9.24,7 12,7 C14.76,7 17,9.24 17,12 C17,14.76 14.76,17 12,17 Z M12,9 C10.34,9 9,10.34 9,12 C9,13.66 10.34,15 12,15 C13.66,15 15,13.66 15,12 C15,10.34 13.66,9 12,9 Z"
                        fill="#FF5E8A"
                      />
                    </g>
                  </svg>
                </span>
              </button>

              {/* Edit Button */}
              <button
                type="button"
                className="btn btn-icon btn-sm btn-pill btn-outline-info mr-2"
                title="Edit Basket"
                onClick={() =>
                  this.props.history.push(`/manage-baskets/${basket.id}/edit`)
                }
              >
                <span className="svg-icon svg-icon-md">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24px"
                    height="24px"
                    viewBox="0 0 24 24"
                  >
                    <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                      <rect x={0} y={0} width={24} height={24} />
                      <path
                        d="M12.2674799,18.2323597 L12.0084872,5.45852451 C12.0004303,5.06114792 12.1504154,4.6768183 12.4255037,4.38993949 L15.0030167,1.70195304 L17.5910752,4.40093695 C17.8599071,4.6812911 18.0095067,5.05499603 18.0## 5.4407122 L18.0## 18.2315498 C18.0## 19.1605425 17.2## 19.9169# 16.3## 19.9169# L13.9## 19.9169# C13.0## 19.9169# 12.2## 19.1605425 12.2## 18.2315498 Z"
                        fill="#17a2b8"
                        opacity="0.3"
                        transform="translate(15.060938, 10.657067) rotate(-135.000000) translate(-15.060938, -10.657067)"
                      />
                      <path
                        d="M3,16 L5,16 C5.55228475,16 6,16.4477153 6,17 C6,17.5522847 5.55228475,18 5,18 L3,18 C2.44771525,18 2,17.5522847 2,17 C2,16.4477153 2.44771525,16 3,16 Z M9,16 L21,16 C21.5522847,16 22,16.4477153 22,17 C22,17.5522847 21.5522847,18 21,18 L9,18 C8.44771525,18 8,17.5522847 8,17 C8,16.4477153 8.44771525,16 9,16 Z"
                        fill="#17a2b8"
                      />
                    </g>
                  </svg>
                </span>
              </button>

              {/* Toggle Visibility Button */}
              <button
                type="button"
                className={`btn btn-icon btn-sm btn-pill mr-2 ${
                  basket.is_active
                    ? "btn-outline-warning"
                    : "btn-outline-success"
                }`}
                title={basket.is_active ? "Deactivate" : "Activate"}
                onClick={() => this._toggleBasketVisibility(basket)}
              >
                <span className="svg-icon svg-icon-md">
                  {basket.is_active ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24px"
                      height="24px"
                      viewBox="0 0 24 24"
                    >
                      <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                        <rect x={0} y={0} width={24} height={24} />
                        <path
                          d="M6,6 L18,18 M6,18 L18,6"
                          stroke="#FFA800"
                          strokeWidth={2}
                          strokeLinecap="round"
                        />
                      </g>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24px"
                      height="24px"
                      viewBox="0 0 24 24"
                    >
                      <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                        <rect x={0} y={0} width={24} height={24} />
                        <path
                          d="M9,12 L11,14 L15,10"
                          stroke="#28a745"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    </svg>
                  )}
                </span>
              </button>

              {/* Delete Button */}
              <button
                type="button"
                className="btn btn-icon btn-sm btn-pill btn-outline-danger"
                title="Delete Basket"
                onClick={() => this._deleteBasket(basket)}
              >
                <span className="svg-icon svg-icon-md">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24px"
                    height="24px"
                    viewBox="0 0 24 24"
                  >
                    <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                      <rect x={0} y={0} width={24} height={24} />
                      <path
                        d="M6,8 L18,8 L17,20 C17,20.5523 16.5523,21 16,21 L8,21 C7.4477,21 7,20.5523 7,20 L6,8 Z M8,10 L8,19 L16,19 L16,10 L8,10 Z"
                        fill="#F64E60"
                      />
                      <path
                        d="M14,4 L14,3 C14,2.4477 13.5523,2 13,2 L11,2 C10.4477,2 10,2.4477 10,3 L10,4 L5,4 C4.4477,4 4,4.4477 4,5 L4,6 C4,6.5523 4.4477,7 5,7 L19,7 C19.5523,7 20,6.5523 20,6 L20,5 C20,4.4477 19.5523,4 19,4 L14,4 Z"
                        fill="#F64E60"
                        opacity="0.3"
                      />
                    </g>
                  </svg>
                </span>
              </button>
            </div>
          );
        },
      },
    ];
  }

  _handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  _handleFilterTypeChange = (e) => {
    this.setState({ filterType: e.target.value });
  };

  _handleFilterStatusChange = (e) => {
    this.setState({ filterStatus: e.target.value });
  };

  _renderFilters() {
    const { searchTerm, filterType, filterStatus } = this.state;

    return (
      <div className="card-header border-0 py-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label font-weight-bolder text-dark">
            Hampers & Party Baskets
          </span>
          <span className="text-muted mt-3 font-weight-bold font-size-sm">
            Manage all baskets and bundles
          </span>
        </h3>
        <div className="card-toolbar">
          <div className="d-flex align-items-center">
            {/* Search Input */}
            <div className="input-group input-group-sm mr-3" style={{ width: 200 }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search baskets..."
                value={searchTerm}
                onChange={this._handleSearchChange}
              />
            </div>

            {/* Type Filter */}
            <select
              className="form-control form-control-sm mr-3"
              style={{ width: 150 }}
              value={filterType}
              onChange={this._handleFilterTypeChange}
            >
              {BASKET_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              className="form-control form-control-sm"
              style={{ width: 120 }}
              value={filterStatus}
              onChange={this._handleFilterStatusChange}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { searchTerm, filterType, filterStatus } = this.state;

    // Build extra params for filtering
    const extraParams = {};
    if (filterType) extraParams.type = filterType;
    if (filterStatus) extraParams.is_active = filterStatus;

    return (
      <div className="card card-custom">
        {this._renderFilters()}
        <div className="card-body pt-0">
          <BaseTable
            ref={this.table}
            endpoint={window.Api.Baskets}
            columns={this._getColumns()}
            searchTerm={searchTerm}
            extraParams={extraParams}
            noDataText="No baskets found"
            defaultPageSize={10}
          />
        </div>
      </div>
    );
  }
}

export default withRouter(BasketsTable);
